import React, { useCallback, useState } from 'react'
import styled from 'styled-components'

import TextInput from 'components/TextInput'
import { Button, type ButtonTheme } from '@nexiahome/ui-common/components/primitives/buttons'
import { ComponentStyleSheet } from '@nexiahome/ui-common/themes'
import SearchIcon from '@mui/icons-material/Search'

import DeviceAggregatorTabs from 'pages/DeviceAggregator/Tabs'

import { useServerErrorContext } from 'hooks/ServerErrorContext'

import { isDeviceIdValid } from 'helpers/validations'

import { screenSize, PageTitle, SinglePanePage } from 'styles/shared'
import { color } from 'styles/colors'

import Api from 'services/api'

import { DeviceBase } from 'types/Device'

const DeviceAggregator = () => {
  const [deviceId, setDeviceId] = useState('')
  const [searchInProgress, setSearchInProgress] = useState(false)
  const [device, setDevice] = useState<DeviceBase | null>(null)
  const [error, setError] = useState('')

  const { setServerError } = useServerErrorContext()

  const onClickSearch = useCallback(async () => {
    if (!isDeviceIdValid(deviceId)) {
      setError('Invalid Device AUID or Serial Number')

      return
    }

    if (device?.deviceId === deviceId) {
      return // device already loaded
    }

    setSearchInProgress(true)

    await Api.Devices.search([deviceId])
      .then(res => {
        if (res?.devices?.length) {
          setDevice(res.devices[0])
        }

        setError(res?.invalidDeviceIds?.length ? 'Device Not Found' : '')
      }).catch(() => {
        setServerError('Failed to get device')
        setDevice(null)
        setError('')
      })
      .finally(() => {
        setSearchInProgress(false)
      })
  }, [device, deviceId, setServerError])

  const onChangeDeviceId = (id: string) => {
    if (id.trim() !== deviceId) {
      setDeviceId(id.trim())
      setDevice(null)
    }
  }

  return (
    <SinglePanePage>
      <PageTitle>Device Aggregator</PageTitle>

      <S.TopContainer>
        <S.Search>
          <TextInput label="Device AUID or Serial Number" value={deviceId} errorMessage={error} onChange={(event) => onChangeDeviceId(event.target.value.toUpperCase())} />
          <Button decoration="positive" enabled={!searchInProgress} styles={searchButtonStyle} onPress={onClickSearch}>
            <S.SearchIcon />
          </Button>
        </S.Search>
      </S.TopContainer>

      {device && <DeviceAggregatorTabs deviceId={device.deviceId} timeZone={device.timeZone} />}
    </SinglePanePage>
  )
}

export default DeviceAggregator

const searchButtonStyle: ComponentStyleSheet<ButtonTheme> = {
  button: {
    height: 39,
    minWidth: 25,
    paddingRight: 8,
    paddingLeft: 8,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 5,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 5,
    marginTop: 5
  }
}

const S = {
  TopContainer: styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 10px;

    @media ${screenSize.small} {
      flex-direction: column;
    }
  `,
  Search: styled.div`
    display: flex;
  `,
  SearchIcon: styled(SearchIcon)`
    color: ${color('primaryButtonText')};
    font-size: 23px;
  `
}
